"use client"

import type React from "react"
import { useState, useMemo } from "react"
import { X, Save, Loader2 } from "lucide-react"
import type { DynamicUser, DynamicSchema, DynamicFormData, DynamicUpdateRequest, DynamicUpdatePayload } from "../types/dynamic"
import { updateDynamicUserByUniqueField } from "../services/api"
import { addToQueue } from "../services/queue"
import { updateDynamicUserInDB } from "../services/database"
import { SchemaService } from "../services/schema"
import { useNetworkStatus } from "../hooks/useNetworkStatus"
import { AccordionField } from "./AccordionField"
import { BadgeColorTable } from "./BadgeColorTable"

interface DynamicEditModalProps {
  user: DynamicUser
  schema: DynamicSchema
  onClose: () => void
  onSuccess: (updatedUser: DynamicUser) => void
  showToast: (type: "success" | "error" | "info", message: string) => void
  cancelCurrentFetch: () => void
}

// Define las propiedades del acordeón
type LocalAccordion = {
  name: string
  label: string
  position: number
  content: React.ReactNode
}

const LOCAL_ACCORDIONS: LocalAccordion[] = [
  {
    name: "ac_info_general",
    label: "Información general de los colores del gafete",
    position: 0,
    content: (
      <>
        <BadgeColorTable />
      </>
    ),
  },
]

const DynamicEditModal: React.FC<DynamicEditModalProps> = ({
  user,
  schema,
  onClose,
  onSuccess,
  showToast,
  cancelCurrentFetch,
}) => {
  const [loading, setLoading] = useState(false)
  const isOnline = useNetworkStatus()
  
  // Obtener campos de edición del schema (visibles y panelCreated)
  const editableFields = SchemaService.getCreationFields(schema)
  
  // Inicializar datos del formulario con valores actuales del usuario
  const [formData, setFormData] = useState<DynamicFormData>(() => {
    const initialData: DynamicFormData = {}
    
    editableFields.forEach(({ fieldName }) => {
      const currentValue = SchemaService.getFieldValue(user, fieldName)
      initialData[fieldName] = currentValue ?? ''
    })
    
    return initialData
  })
  
  // Datos originales para detectar cambios
  const [originalData] = useState<DynamicFormData>(() => {
    const original: DynamicFormData = {}
    
    editableFields.forEach(({ fieldName }) => {
      const currentValue = SchemaService.getFieldValue(user, fieldName)
      original[fieldName] = currentValue ?? ''
    })
    
    return original
  })
  
  const [errors, setErrors] = useState<{ [field: string]: string }>({})

  //---------------------------------- ACORDEON ---------------------------------------
  // Construir la lista de campos que vas a renderizar (schema + acordeones)
  const creationFieldsWithAccordions = useMemo(() => {
    const BEFORE_FIELDS = ["badge_color", "categoria"]

    const minMarkerPos = Math.min(
      ...BEFORE_FIELDS.map(n => schema[n]?.position ?? 999)
    )

    const accordionAsFields = LOCAL_ACCORDIONS.map(ac => ({
      fieldName: ac.name,
      fieldSchema: {
        type: "accordion",
        label: ac.label,
        position: (minMarkerPos === 999 ? 0 : minMarkerPos - 0.001),
        editable: false,
        required: false,
        visible: true,
      },
    }))

    return [...accordionAsFields, ...editableFields].sort((a, b) => {
      const pa = a.fieldSchema.position ?? 999
      const pb = b.fieldSchema.position ?? 999
      if (pa !== pb) return pa - pb
      // Si la posición es igual, priorizar acordeones
      if (a.fieldSchema.type === "accordion" && b.fieldSchema.type !== "accordion") return -1
      if (b.fieldSchema.type === "accordion" && a.fieldSchema.type !== "accordion") return 1
      return 0
    })
  }, [schema, editableFields])
  //-------------------------------------------------------------------------------------

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    let processedValue: any = value

    // Procesar valor según el tipo de campo
    const fieldConfig = schema[name]
    if (fieldConfig) {
      switch (fieldConfig.type) {
        case 'number':
          processedValue = value === '' ? 0 : Number(value)
          break
        case 'boolean':
          processedValue = (e.target as HTMLInputElement).checked
          break
        case 'string':
        case 'enum':
        default:
          // Normalización específica por campo
          if (name === 'email') {
            processedValue = value.toLowerCase().trim()
          } else if (name === 'telefono') {
            processedValue = value.replace(/[^\d\s()+-]/g, "")
          } else if (name === 'nombre' || name === 'apellido') {
            processedValue = value.replace(/\s+/g, " ")
          } else {
            processedValue = value
          }
          break
      }
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }))

    // Limpiar error cuando el usuario comience a escribir
    if (errors[name]) {
      setErrors(prev => {
        const { [name]: removedError, ...rest } = prev
        return rest
      })
    }
  }

  const validateForm = (): boolean => {
    const validationErrors = SchemaService.validateFormData(formData, schema)
    const errorMap: { [field: string]: string } = {}

    validationErrors.forEach(error => {
      errorMap[error.field] = error.message
    })

    setErrors(errorMap)
    return validationErrors.length === 0
  }

  // Detectar cambios para enviar solo campos modificados
  const getChangedFields = (): DynamicUpdateRequest => {
    const changes: DynamicUpdateRequest = {}
    const mainFields = ['name', 'last_name', 'email', 'phone_number', 'folio', 'printed', 'print_date', 'number_of_prints']
    const extraChanges: Record<string, any> = {}

    editableFields.forEach(({ fieldName }) => {
      const currentValue = formData[fieldName]
      const originalValue = originalData[fieldName]
      
      // Detectar cambios
      if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
        if (mainFields.includes(fieldName)) {
          // Campo principal
          changes[fieldName as keyof DynamicUpdateRequest] = currentValue
        } else {
          // Campo extra
          extraChanges[fieldName] = currentValue
        }
      }
    })

    // Solo agregar extra si hay cambios
    if (Object.keys(extraChanges).length > 0) {
      changes.extra = extraChanges
    }

    return changes
  }

  const hasChanges = (): boolean => {
    const changes = getChangedFields()
    return Object.keys(changes).length > 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      showToast("error", "Por favor corrige los errores en el formulario")
      return
    }

    if (!hasChanges()) {
      showToast("info", "No hay cambios para guardar")
      onClose()
      return
    }

    // Cancelar cualquier descarga en curso
    cancelCurrentFetch()
    setLoading(true)

    try {
      // Obtener solo los campos modificados
      const updateData = getChangedFields()
      
      // Obtener campo único
      const uniqueField = SchemaService.getUniqueField(schema)
      if (!uniqueField) {
        throw new Error("No se encontró campo único para la actualización")
      }
      
      const uniqueFieldValue = SchemaService.getFieldValue(user, uniqueField)
      if (!uniqueFieldValue) {
        throw new Error(`Valor del campo único ${uniqueField} no encontrado`)
      }

      console.log(`💾 Actualizando usuario con campo único ${uniqueField}: ${uniqueFieldValue}`)
      console.log('📝 Cambios a enviar:', updateData)

      // Crear usuario actualizado para UI optimista
      const updatedUser: DynamicUser = {
        ...user,
        // Aplicar cambios principales
        ...(updateData.name && { name: updateData.name }),
        ...(updateData.last_name && { last_name: updateData.last_name }),
        ...(updateData.email && { email: updateData.email }),
        ...(updateData.phone_number && { phone_number: updateData.phone_number }),
        // Aplicar cambios extra
        ...(updateData.extra && {
          extra: {
            ...user.extra,
            ...updateData.extra
          }
        })
      }

      // Actualización optimista en IndexedDB
      await updateDynamicUserInDB(updatedUser)
      onSuccess(updatedUser)
      onClose() // Cerrar modal inmediatamente

      // Crear payload para API
      const updatePayload: DynamicUpdatePayload = {
        uniqueFieldValue: String(uniqueFieldValue),
        updateData
      }

      // Manejar actualización en segundo plano
      const syncUpdate = async () => {
        if (isOnline) {
          try {
            await updateDynamicUserByUniqueField(updatePayload)
            showToast("success", `Usuario actualizado exitosamente`)
          } catch (networkError) {
            console.warn("Error de red, agregando a cola:", networkError)
            await addToQueue({
              type: "update_dynamic_user",
              data: updatePayload,
              timestamp: Date.now(),
            })
            showToast("info", "Cambios guardados localmente, se sincronizarán cuando haya conexión")
          }
        } else {
          await addToQueue({
            type: "update_dynamic_user",
            data: updatePayload,
            timestamp: Date.now(),
          })
          showToast("info", "Cambios guardados localmente, se sincronizarán cuando haya conexión")
        }
      }

      // Ejecutar sincronización en segundo plano (muestra el único aviso: guardado en servidor o local)
      syncUpdate().catch(console.error)
    } catch (error) {
      console.error("❌ Error actualizando usuario dinámico:", error)
      showToast("error", "Error al actualizar el usuario")
    } finally {
      setLoading(false)
    }
  }


  const renderField = (fieldName: string, fieldConfig: any, _index: number) => {
    const value = formData[fieldName]
    const error = errors[fieldName]
    const isRequired = fieldConfig.required
    const isEditable = fieldConfig.editable !== false
    const inputId = `edicion-${fieldName}`

    // Renderizar acordeón
    if (fieldConfig.type === "accordion") {
      const ac = LOCAL_ACCORDIONS.find(a => a.name === fieldName)
      return (
        <div key={fieldName} className="col-span-full">
          <AccordionField label={fieldConfig.label}>
            {ac ? ac.content : "Contenido no disponible."}
          </AccordionField>
        </div>
      )
    }

    // Mostrar indicador si el campo ha cambiado
    const hasChanged = JSON.stringify(value) !== JSON.stringify(originalData[fieldName])
    
    // Detectar campos vacíos para aplicar estilo de warning
    const isEmpty = !value || String(value).trim() === ''
    const showEmptyWarning = isEmpty && !error // Solo si no hay otro error

    return (
      <div key={fieldName}>
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
          {fieldConfig.label}{" "}
          {isRequired && <span className="text-red-500">*</span>}
          {hasChanged && <span className="text-blue-500 text-xs ml-1">(modificado)</span>}
        </label>

        {/* Renderizado según tipo de campo */}
        {fieldConfig.type === 'enum' ? (
          <>
            <select
              id={inputId}
              name={fieldName}
              value={value}
              onChange={handleChange}
              disabled={!isEditable || loading}
              className={`w-full bg-white border-2 border-gray-300 text-gray-800 rounded-xl px-3 py-2 focus:bg-blue-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-300 ${
                error ? "border-red-500 bg-red-50" : 
                hasChanged ? "border-blue-500 bg-blue-50" : 
                showEmptyWarning ? "border-amber-300 bg-amber-200/10" : ""
              } ${!isEditable ? "bg-gray-200 cursor-not-allowed" : ""}`}
            >
              <option value="">Seleccionar...</option>
              {fieldConfig.options?.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            
            {/* Advertencia para valores no válidos de enum */}
            {(() => {
              const currentValue = String(value || '').trim()
              const hasValue = currentValue !== ''
              const validOptions = fieldConfig.options || []
              const isValidOption = validOptions.includes(currentValue)
              const showWarning = hasValue && !isValidOption
              
              return showWarning ? (
                <div className="mt-1 p-2 bg-amber-600/10 backdrop-blur-sm border border-amber-400/20 rounded-xl">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-4 w-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-2">
                      <p className="text-xs text-amber-700">
                        <strong>Valor no válido:</strong> "{currentValue}" no coincide con las opciones predeterminadas.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null
            })()}
          </>
        ) : fieldConfig.type === 'boolean' ? (
          <div className="flex items-center">
            <input
              id={inputId}
              type="checkbox"
              name={fieldName}
              checked={Boolean(value)}
              onChange={handleChange}
              disabled={!isEditable || loading}
              className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                !isEditable ? "cursor-not-allowed" : ""
              }`}
            />
            <span className="ml-2 text-sm text-gray-600">
              {fieldConfig.label}
              {hasChanged && <span className="text-blue-500 ml-1">(modificado)</span>}
            </span>
          </div>
        ) : fieldConfig.type === 'number' ? (
          <input
            id={inputId}
            type="number"
            name={fieldName}
            value={value}
            onChange={handleChange}
            disabled={!isEditable || loading}
            className={`w-full bg-white border-2 border-gray-300 text-gray-800 rounded-xl px-3 py-2 focus:bg-blue-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-300 ${
              error ? "border-red-500 bg-red-50" : 
              hasChanged ? "border-blue-500 bg-blue-50" : 
              showEmptyWarning ? "border-amber-300 bg-amber-200/10" : ""
            } ${!isEditable ? "bg-gray-200 cursor-not-allowed" : ""}`}
          />
        ) : (
          <input
            id={inputId}
            type={fieldName === 'email' ? 'email' : fieldName === 'telefono' ? 'tel' : 'text'}
            name={fieldName}
            value={value}
            onChange={handleChange}
            disabled={!isEditable || loading}
            maxLength={255}
            placeholder={fieldName === 'telefono' ? '+52 555 123 4567' : ''}
            className={`w-full bg-white border-2 border-gray-300 text-gray-800 rounded-xl px-3 py-2 focus:bg-blue-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-300 placeholder-gray-500 ${
              error ? "border-red-500 bg-red-50" : 
              hasChanged ? "border-blue-500 bg-blue-50" : 
              showEmptyWarning ? "border-amber-300 bg-amber-200/10" : ""
            } ${!isEditable ? "bg-gray-200 cursor-not-allowed" : ""}`}
          />
        )}

        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white/40 backdrop-blur-sm px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Editar Usuario Dinámico
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Actualizar el .map() del formulario para usar la lista combinada */}
              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {creationFieldsWithAccordions.map(({ fieldName, fieldSchema }, index) =>
                renderField(fieldName, fieldSchema, index)
              )}
              </div> */}
              
              {/* Información del usuario */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600">
                  <strong>ID:</strong> {user.id} | 
                  <strong> Campo único:</strong> {SchemaService.getUniqueField(schema) || 'No definido'}
                </p>
              </div>

              {/* Formulario en 2 columnas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {creationFieldsWithAccordions.map(({ fieldName, fieldSchema }, index) =>
                renderField(fieldName, fieldSchema, index)
              )}
              </div>

              {/* Advertencia de conexión */}
              {!isOnline && (
                <div className="p-4 rounded-md bg-yellow-50 border border-yellow-200">
                  <div className="flex items-center">
                    <div className="text-yellow-600 mr-2">⚠️</div>
                    <div className="text-sm text-yellow-800">
                      <strong>Sin conexión:</strong> Los cambios se guardarán localmente y se sincronizarán cuando haya conexión.
                    </div>
                  </div>
                </div>
              )}

              {/* Información de cambios */}
              {hasChanges() && (
                <div className="p-4 rounded-md bg-blue-50 border border-blue-200">
                  <div className="text-sm text-blue-800">
                    <strong>Cambios detectados:</strong> Solo se enviarán los campos modificados al servidor.
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !hasChanges()}
                  className="inline-flex items-center px-4 py-2 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 text-blue-800 text-sm font-medium rounded-xl hover:bg-blue-600/30 hover:border-blue-500/40 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DynamicEditModal
